import { FileSpreadsheet, Sparkles } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import { cn } from "@/core/utils/utils";
import type { MarketingProduct } from "@/product-marketing/types";
import { PRODUCT_MARKETING } from "./content";

export const ProductExample = ({ product }: { product: MarketingProduct }) => {
  const content = PRODUCT_MARKETING[product];
  return (
    <figure
      aria-label={`Example ${content.name} preview`}
      className="w-full min-w-0 overflow-hidden rounded-2xl border border-gray-80 bg-gray-100 font-inter leading-4 text-white shadow-xl"
    >
      <Stack
        direction="row"
        align="center"
        justify="between"
        fullWidth
        className="border-b border-white/10 px-6 py-3"
      >
        <Stack direction="row" align="center" gap="sm">
          <FileSpreadsheet className="h-4 w-4 text-orange-40" aria-hidden />
          <span className="text-md font-semibold">{content.previewTitle}</span>
        </Stack>
        <span className="rounded border border-white/20 px-2 py-1 text-sm font-medium uppercase tracking-wider text-white/80">
          Example
        </span>
      </Stack>
      <Stack gap="none" fullWidth className="px-6 pb-4 pt-4">
        <p className="text-sm text-white/70">{content.previewLabel}</p>
        <dl className="mb-2 mt-3 grid w-full grid-cols-3 gap-4">
          {content.metrics.map(({ label, value }, index) => (
            <Stack key={label} gap="xs">
              <dt className="text-sm text-white/75">{label}</dt>
              <dd
                className={cn(
                  "text-3xl font-semibold tabular-nums leading-8 tracking-tight",
                  { "text-orange-40": index === 2 },
                )}
              >
                {value}
              </dd>
            </Stack>
          ))}
        </dl>
        <Table className="mt-4 text-sm tabular-nums">
          <TableHeader className="static">
            <TableRow className="border-b border-white/20 hover:bg-transparent">
              {content.columns.map((column, index) => (
                <TableHead
                  key={column}
                  scope="col"
                  className={cn("h-auto px-0 pb-2 font-medium text-white/70", {
                    "text-right": index > 0,
                  })}
                >
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {content.rows.map((row) => (
              <TableRow key={row[0]} className="hover:bg-transparent">
                {row.map((cell, index) => (
                  <TableCell
                    key={index}
                    className={cn("border-white/10 px-0 py-2 text-white/85", {
                      "text-right text-white/75": index > 0,
                      "text-orange-40": index === 3,
                    })}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack
          gap="sm"
          fullWidth
          className="mt-4 rounded-lg border border-orange-50/20 bg-orange-50/5 px-4 py-3"
        >
          <Stack direction="row" align="center" gap="sm">
            <Sparkles className="h-4 w-4 text-orange-40" aria-hidden />
            <span className="text-sm font-medium uppercase tracking-wider text-orange-40">
              {content.previewCalloutTitle}
            </span>
          </Stack>
          <p className="text-md leading-5 text-white/90">
            {content.previewNote}
          </p>
        </Stack>
        <figcaption className="mt-4 text-sm leading-4 text-white/70">
          Illustrative data. Your{" "}
          {product === "payroll" ? "payroll" : "invoice review"} is configured
          for your business.
        </figcaption>
      </Stack>
    </figure>
  );
};
